import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { AlertTriangle, Banknote, Car, Gift, Star, TrendingUp, User, XCircle } from 'lucide-react';
import { SkeletonPage, Skeleton, CHART, chartTooltip } from '../ui/kit';

export default function DriverStats() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDriver, setSelectedDriver] = useState('all');
  const [selectedWeek, setSelectedWeek] = useState(null); // null = current week
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [useCustom, setUseCustom] = useState(false);

  const load = (params = {}) => {
    setLoading(true);
    const q = new URLSearchParams();
    if (params.driver_id && params.driver_id !== 'all') q.set('driver_id', params.driver_id);
    if (params.from) q.set('from', params.from);
    if (params.to) q.set('to', params.to);
    api.get(`/partners/driver-stats?${q}`).then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const applyFilter = () => {
    const params = { driver_id: selectedDriver };
    if (useCustom && customFrom && customTo) {
      params.from = customFrom; params.to = customTo;
    } else if (selectedWeek) {
      params.from = selectedWeek.from; params.to = selectedWeek.to;
    }
    load(params);
  };

  useEffect(() => { applyFilter(); }, [selectedDriver, selectedWeek, useCustom]);

  if (!data && loading) return <SkeletonPage />;

  const weeks = data?.weeks || [];
  const drivers = data?.drivers || [];
  const allDriversList = data?.drivers?.map(d => d.driver) || [];

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 'var(--vg-fs-title)', fontWeight: 900, marginBottom: 4 }}>Fahrer-Umsatz</h1>
        <p style={{ color: '#6b7280', fontSize: 13 }}>Detaillierte Auswertung pro Fahrer</p>
      </div>

      {/* Filter Row */}
      <div style={{ background: '#fff', borderRadius: 12, padding: '14px 16px', marginBottom: 20, border: '1px solid #e5e7eb', display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        {/* Driver selector */}
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>Fahrer</div>
          <select value={selectedDriver} onChange={e => setSelectedDriver(e.target.value)}
            style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 13, fontWeight: 600, background: '#fff', cursor: 'pointer', minWidth: 160 }}>
            <option value="all">Alle Fahrer</option>
            {allDriversList.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </div>

        {/* Week selector */}
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>Betriebswoche · 04:00–03:59</div>
          <select
            /* GELDKETTE-KORREKTUR-001 REVIEW-FIX: „Diese Woche" stand zweimal da — einmal als
               fest verdrahteter Leer-Eintrag und einmal aus der Serverliste. Seit der Server den
               Standard auf dieselbe Betriebswoche stellt, meinen beide denselben Zeitraum.
               Der Leer-Eintrag ist deshalb weg; ohne Auswahl steht die laufende Woche der Liste. */
            value={selectedWeek ? selectedWeek.from : ((weeks.find(w => w.current) || weeks[0] || {}).from || '')}
            onChange={e => {
              const w = weeks.find(w => w.from === e.target.value);
              setSelectedWeek(w || null); setUseCustom(false);
            }}
            style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 13, fontWeight: 600, background: '#fff', cursor: 'pointer', minWidth: 180 }}>
            {weeks.map(w => <option key={w.from} value={w.from}>{w.label} ({w.from} – {w.to})</option>)}
          </select>
        </div>

        {/* Custom date range */}
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>Eigener Zeitraum</div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input type="date" value={customFrom} onChange={e => { setCustomFrom(e.target.value); setUseCustom(true); setSelectedWeek(null); }}
              style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 13 }} />
            <span style={{ color: '#6b7280', fontSize: 13 }}>bis</span>
            <input type="date" value={customTo} onChange={e => { setCustomTo(e.target.value); setUseCustom(true); setSelectedWeek(null); }}
              style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 13 }} />
            {useCustom && customFrom && customTo && (
              <button onClick={applyFilter} style={{ padding: '7px 14px', background: '#000', color: '#fff', borderRadius: 8, fontSize: 13, fontWeight: 700, border: 'none', cursor: 'pointer' }}>→</button>
            )}
          </div>
        </div>
      </div>

      {loading && <Skeleton h={120} r={12} style={{ marginBottom: 16 }} />}

      {!loading && drivers.map(({ driver, stats, daily }) => (
        <div key={driver.id} style={{ background: '#fff', borderRadius: 14, border: '1px solid #e5e7eb', marginBottom: 16, overflow: 'hidden' }}>
          {/* Driver header */}
          <div style={{ padding: '14px 18px', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 38, height: 38, borderRadius: 10, background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <User size={18} color="#4b5563" />
              </div>
              <div>
                <div style={{ fontWeight: 800, fontSize: 16 }}>{driver.name}</div>
                <div style={{ fontSize: 12, color: '#6b7280' }}>
                  <Star size={13} style={{ verticalAlign: -2, marginRight: 6 }} /> {parseFloat(driver.rating || 5).toFixed(1)} · {driver.is_online ? 'Online' : 'Offline'}
                </div>
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 22, fontWeight: 900, color: '#111' }}>{(stats.netto || 0).toFixed(2)} €</div>
              <div style={{ fontSize: 12, color: '#6b7280' }}>Kontobewegung bei VOOGO · keine Auszahlung</div>
            </div>
          </div>

          {/* Stats grid */}
          <div style={{ padding: '14px 18px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 16 }}>
              {[
                { label: 'Fahrten', value: stats.rides || 0, color: '#4b5563', icon: Car },
                { label: 'Bargeld', value: `${(stats.cash || 0).toFixed(2)} €`, color: '#9C27B0', icon: Banknote },
                { label: 'Trinkgeld', value: `${(stats.tips || 0).toFixed(2)} €`, color: '#4b5563', icon: Gift },
                { label: 'Partnerumsatz', value: `${(stats.revenue || 0).toFixed(2)} €`, color: '#4b5563', icon: TrendingUp },
              ].map(({ label, value, color, icon: Icon }) => (
                <div key={label} style={{ background: '#f9fafb', borderRadius: 10, padding: '12px 10px', textAlign: 'center' }}>
                  <div style={{ fontSize: 22, fontWeight: 800, color }}>{value}</div>
                  <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{label}</div>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 12, marginBottom: daily.length > 0 ? 16 : 0, flexWrap: 'wrap' }}>
              {stats.cancelled > 0 && <span style={{ fontSize: 12, color: '#ef4444', background: '#fef2f2', padding: '3px 10px', borderRadius: 20 }}><XCircle size={13} style={{ verticalAlign: -2, marginRight: 6 }} /> {stats.cancelled} Storniert</span>}
              {stats.no_shows > 0 && <span style={{ fontSize: 12, color: '#4b5563', background: '#f3f4f6', padding: '3px 10px', borderRadius: 20 }}><AlertTriangle size={13} style={{ verticalAlign: -2, marginRight: 6 }} /> {stats.no_shows} No-Show</span>}
            </div>

            {/* Daily chart */}
            {daily.length > 0 && (
              <div style={{ marginTop: 4 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>Tagesumsatz</div>
                <ResponsiveContainer width="100%" height={120}>
                  <BarChart data={daily} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                    <CartesianGrid vertical={false} strokeDasharray="3 3" stroke={CHART.grid} />
                    <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fill: CHART.axis, fontSize: 11 }} tickFormatter={d => d?.slice(5)} />
                    <YAxis axisLine={false} tickLine={false} tick={{ fill: CHART.axis, fontSize: 11 }} />
                    <Tooltip cursor={{ fill: 'rgba(0,0,0,0.04)' }} contentStyle={chartTooltip} formatter={v => [`${Number(v).toFixed(2)} €`]} />
                    <Bar dataKey="revenue" fill={CHART.ink} radius={CHART.barRadius} />
                    <Bar dataKey="tips" fill="#4b5563" radius={CHART.barRadius} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>
      ))}

      {!loading && drivers.length === 0 && (
        <div style={{ textAlign: 'center', padding: 60, color: '#6b7280' }}>
          <Car size={48} style={{ marginBottom: 12, opacity: 0.3 }} />
          <div>Keine Fahrer gefunden</div>
        </div>
      )}
    </div>
  );
}
